import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';

export interface LoadingOperation {
  id: string;
  message: string;
  startTime: number;
  estimatedDurationMs: number;
  progress: number;
  /** Whether the operation has outlived the debounce window and should be shown. */
  visible: boolean;
}

export interface LoadingState {
  isLoading: boolean;
  operations: LoadingOperation[];
  activeMessage: string;
  overallProgress: number;
}

@Injectable({ providedIn: 'root' })
export class LoadingService {
  private _operations = new Map<string, LoadingOperation>();
  private _state$ = new BehaviorSubject<LoadingState>({
    isLoading: false,
    operations: [],
    activeMessage: 'Loading\u2026',
    overallProgress: 0
  });

  readonly state$ = this._state$.asObservable();

  /** Backward-compatible boolean observable */
  readonly isLoading$ = this.state$.pipe(map(s => s.isLoading));

  private _idCounter = 0;
  private _progressTimer: ReturnType<typeof setInterval> | null = null;
  private _cycleTimer: ReturnType<typeof setInterval> | null = null;
  private _currentMessageIndex = 0;

  /** How long an operation must run before its overlay is actually revealed. */
  private readonly _showDelayMs = 250;
  private _visibilityTimers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(private zone: NgZone) {}

  /**
   * Start a loading operation.
   * @param message  Status text shown in the overlay (default: "Loading…")
   * @param estimatedMs  Estimated duration in ms for progress curve
   * @returns Operation ID to pass to hide()
   */
  show(message?: string, estimatedMs?: number): string {
    const id = `op_${++this._idCounter}`;
    const msg = message || 'Loading\u2026';
    this._operations.set(id, {
      id,
      message: msg,
      startTime: Date.now(),
      estimatedDurationMs: estimatedMs ?? this._defaultDuration(msg),
      progress: 0,
      visible: false
    });

    // Defer revealing the overlay: only show it if the operation is still running after
    // the debounce window. Fast requests (the common case when clicking around) finish
    // first and are hidden before they ever flash a blocking spinner.
    this.zone.runOutsideAngular(() => {
      const t = setTimeout(() => {
        this._visibilityTimers.delete(id);
        const op = this._operations.get(id);
        if (!op) return;
        op.visible = true;
        op.startTime = Date.now(); // start the progress curve from the moment it appears
        this._ensureTimers();
        this.zone.run(() => this._emit());
      }, this._showDelayMs);
      this._visibilityTimers.set(id, t);
    });

    return id;
  }

  /**
   * Complete a loading operation.
   * @param id  Operation ID from show(). If omitted, removes the oldest operation.
   */
  hide(id?: string): void {
    const key = id && this._operations.has(id)
      ? id
      : this._operations.keys().next().value;

    if (!key || !this._operations.has(key)) return;

    // Cancel a pending reveal — if the operation never became visible, it never showed.
    const vt = this._visibilityTimers.get(key);
    if (vt) { clearTimeout(vt); this._visibilityTimers.delete(key); }

    const op = this._operations.get(key)!;

    if (!op.visible) {
      this._operations.delete(key);
      if (!this._hasVisibleOps()) this._stopTimers();
      this._emit();
      return;
    }

    op.progress = 100;
    this._emit();

    setTimeout(() => {
      this._operations.delete(key);
      if (!this._hasVisibleOps()) this._stopTimers();
      this._emit();
    }, 180);
  }

  /** Force off on navigation errors, etc. */
  reset(): void {
    this._operations.clear();
    this._visibilityTimers.forEach(t => clearTimeout(t));
    this._visibilityTimers.clear();
    this._stopTimers();
    this._currentMessageIndex = 0;
    this._emit();
  }

  private _hasVisibleOps(): boolean {
    for (const op of this._operations.values()) if (op.visible) return true;
    return false;
  }

  // --- Internals ---

  private _defaultDuration(message: string): number {
    const m = message.toLowerCase();
    if (m.includes('download') || m.includes('file')) return 8000;
    if (m.includes('save') || m.includes('process') || m.includes('bulk')) return 5000;
    if (m.includes('grid') || m.includes('query') || m.includes('data')) return 4000;
    if (m.includes('definition') || m.includes('config') || m.includes('layout')) return 2000;
    return 3000;
  }

  private _ensureTimers(): void {
    if (!this._progressTimer) {
      this.zone.runOutsideAngular(() => {
        this._progressTimer = setInterval(() => {
          this._tickProgress();
          this.zone.run(() => this._emit());
        }, 120);
      });
    }
    if (!this._cycleTimer) {
      this.zone.runOutsideAngular(() => {
        this._cycleTimer = setInterval(() => {
          this._cycleTick();
          this.zone.run(() => this._emit());
        }, 2200);
      });
    }
  }

  private _stopTimers(): void {
    if (this._progressTimer) { clearInterval(this._progressTimer); this._progressTimer = null; }
    if (this._cycleTimer) { clearInterval(this._cycleTimer); this._cycleTimer = null; }
  }

  /** Asymptotic progress: advances quickly then slows, never reaches 100 */
  private _tickProgress(): void {
    const now = Date.now();
    this._operations.forEach(op => {
      if (op.progress >= 100) return;
      const elapsed = now - op.startTime;
      const ratio = elapsed / op.estimatedDurationMs;
      op.progress = Math.min(95, 90 * (1 - Math.exp(-2.5 * ratio)));
    });
  }

  /** Cycle through messages when multiple concurrent operations exist */
  private _cycleTick(): void {
    const count = this._operations.size;
    if (count <= 1) {
      this._currentMessageIndex = 0;
      return;
    }
    this._currentMessageIndex = (this._currentMessageIndex + 1) % count;
  }

  private _emit(): void {
    const ops = [...this._operations.values()].filter(o => o.visible);
    const isLoading = ops.length > 0;

    let activeMessage = 'Loading\u2026';
    if (ops.length > 0) {
      const idx = this._currentMessageIndex % ops.length;
      activeMessage = ops[idx]?.message ?? 'Loading\u2026';
    }

    const overallProgress = ops.length === 0
      ? 0
      : ops.reduce((sum, o) => sum + o.progress, 0) / ops.length;

    this._state$.next({ isLoading, operations: ops, activeMessage, overallProgress });
  }
}
